/**
 * Visual-only preview comparing three UX directions for the
 * Seertech home page. Standalone route — no data
 * fetch, no auth side-effects. Used to show before/after candidates
 * without touching production styling.
 *
 * Route: /lie-style-preview  (auth-gated like the rest of the app)
 */
import React from 'react';
import {
  BookOpen, Users, GitCompare, BarChart3, DollarSign,
  AlertTriangle, ArrowRight, Info, Pin, Search, Briefcase, FileText, RotateCw,
} from 'lucide-react';

const PILLARS = [
  { label: 'LEI Health', value: '82%', delta: '+4.6% qoq', state: 'healthy', icon: BookOpen, sub: 'Learning System' },
  { label: 'PE Health', value: '77%', delta: '+4.5% qoq', state: 'healthy', icon: Users, sub: 'People Engine' },
  { label: 'ROE Health', value: '60.9%', delta: '-1.4% qoq', state: 'risk', icon: GitCompare, sub: 'Behavior Transfer' },
  { label: 'KPI Impact', value: '30%', delta: '-12.7% qoq', state: 'critical', icon: BarChart3, sub: 'Business Metrics' },
  { label: 'ROI', value: '7.3x', delta: '+10.5% qoq', state: 'healthy', icon: DollarSign, sub: 'Financial Return' },
];

const VALUE_CHAIN = ['LEI', 'C × M', 'PE Ideal', 'ROE', 'PE Validated', 'KPI', 'ROI', 'Maturity'];

// ─────────────────────────────────────────────────────────────
// (CURRENT) — exact same look as today's LIE home
// ─────────────────────────────────────────────────────────────
function CurrentVariant() {
  const colors = {
    healthy: { card: 'bg-emerald-50 border-emerald-300', text: 'text-emerald-700', badge: 'bg-emerald-500 text-white' },
    risk: { card: 'bg-amber-50 border-amber-400', text: 'text-amber-700', badge: 'bg-amber-500 text-white' },
    critical: { card: 'bg-rose-50 border-rose-400', text: 'text-rose-700', badge: 'bg-rose-500 text-white' },
  };
  return (
    <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm">
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 text-white px-10 py-12">
        <div className="text-[10px] tracking-widest text-teal-700 uppercase mb-2 font-semibold">Closed-loop performance system</div>
        <h1 className="text-3xl font-bold">Seertech</h1>
        <p className="mt-3 text-slate-300 max-w-4xl text-sm leading-relaxed">
          Identify workforce risk, model learning interventions, execute programs, and prove impact — in one continuous cycle.
        </p>
        <div className="mt-6 flex items-center gap-2 text-xs">
          <span className="text-slate-400 uppercase tracking-wider">Value chain</span>
          {VALUE_CHAIN.map((n, i) => (
            <React.Fragment key={n}>
              <span className={`px-2.5 py-1 rounded-md border ${i === 5 ? 'bg-rose-500 border-rose-400 text-white font-semibold' : 'border-slate-200 text-slate-900'}`}>{n}</span>
              {i < VALUE_CHAIN.length - 1 && <span className="text-slate-500">›</span>}
            </React.Fragment>
          ))}
        </div>
      </div>
      <div className="bg-slate-50 px-10 py-8">
        <div className="grid grid-cols-5 gap-4">
          {PILLARS.map(p => {
            const c = colors[p.state];
            const Icon = p.icon;
            return (
              <div key={p.label} className={`rounded-xl border-2 p-4 ${c.card}`}>
                <Icon className={`w-5 h-5 ${c.text}`} />
                <div className="mt-3 text-xs text-slate-700 font-semibold">{p.label}</div>
                <div className={`mt-1 text-3xl font-bold ${c.text}`}>{p.value}</div>
                <div className="text-[11px] text-slate-500 mt-1">{p.sub}</div>
                <div className={`text-[11px] mt-2 ${p.delta.startsWith('-') ? 'text-rose-600' : 'text-emerald-600'}`}>{p.delta}</div>
                <span className={`mt-3 inline-block text-[10px] font-bold px-2 py-0.5 rounded ${c.badge}`}>
                  {p.state === 'healthy' ? 'HEALTHY' : p.state === 'risk' ? 'AT RISK' : 'CRITICAL'}
                </span>
              </div>
            );
          })}
        </div>
        <div className="mt-6 rounded-xl border-2 border-rose-300 bg-rose-50 p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5" />
          <div className="flex-1">
            <div className="text-[10px] tracking-wider uppercase font-bold text-rose-700">Your weak link</div>
            <div className="mt-1 text-sm text-slate-800">
              <span className="font-semibold">Performance & Outcomes</span> is your lowest-scoring pillar at{' '}
              <span className="font-bold text-rose-700">75/100</span>. It caps how much value flows through the rest of the chain.
            </div>
            <div className="mt-2 text-2xl font-bold text-rose-700">$105K – $376K <span className="text-sm text-slate-600 font-normal">/year leaking</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// (A) SOFT REFRESH — same structure, muted chroma
// ─────────────────────────────────────────────────────────────
function SoftRefreshVariant() {
  const colors = {
    healthy: { text: 'text-emerald-700', dot: 'bg-emerald-500', ring: 'ring-emerald-200' },
    risk: { text: 'text-amber-700', dot: 'bg-amber-500', ring: 'ring-amber-200' },
    critical: { text: 'text-rose-700', dot: 'bg-rose-400', ring: 'ring-rose-200' },
  };
  return (
    <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm">
      <div className="bg-slate-50 border-b border-slate-200 px-10 py-10 relative">
        <div className="absolute top-0 left-10 right-10 h-0.5 bg-teal-500/70" />
        <div className="text-[10px] tracking-widest text-teal-600 uppercase mb-2 font-semibold">Closed-loop performance system</div>
        <h1 className="text-3xl font-bold text-slate-900">Seertech</h1>
        <p className="mt-3 text-slate-600 max-w-4xl text-sm leading-relaxed">
          Identify workforce risk, model learning interventions, execute programs, and prove impact — in one continuous cycle.
        </p>
        <div className="mt-6 flex items-center gap-2 text-xs">
          <span className="text-slate-500 uppercase tracking-wider">Value chain</span>
          {VALUE_CHAIN.map((n, i) => (
            <React.Fragment key={n}>
              <span className={`px-2.5 py-1 rounded-md ${i === 5 ? 'border border-rose-300 text-rose-700 bg-rose-50 font-semibold' : 'border border-slate-300 text-slate-600 bg-white'}`}>{n}</span>
              {i < VALUE_CHAIN.length - 1 && <span className="text-slate-300">›</span>}
            </React.Fragment>
          ))}
        </div>
      </div>
      <div className="bg-white px-10 py-8">
        <div className="grid grid-cols-5 gap-4">
          {PILLARS.map(p => {
            const c = colors[p.state];
            const Icon = p.icon;
            return (
              <div key={p.label} className={`rounded-xl bg-white border border-slate-200 p-4 ring-1 ${c.ring}`}>
                <div className="flex items-center justify-between">
                  <Icon className="w-5 h-5 text-slate-500" />
                  <div className="flex items-center gap-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                    <span className="text-[9px] uppercase tracking-wider text-slate-500 font-medium">
                      {p.state === 'healthy' ? 'Healthy' : p.state === 'risk' ? 'At risk' : 'Critical'}
                    </span>
                  </div>
                </div>
                <div className="mt-4 text-xs text-slate-600 font-medium">{p.label}</div>
                <div className={`mt-1 text-3xl font-bold ${c.text}`}>{p.value}</div>
                <div className="text-[11px] text-slate-400 mt-1">{p.sub}</div>
                <div className={`text-[11px] mt-2 ${p.delta.startsWith('-') ? 'text-rose-600' : 'text-emerald-600'}`}>{p.delta}</div>
              </div>
            );
          })}
        </div>
        <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50/50 p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div className="flex-1">
            <div className="text-[10px] tracking-wider uppercase font-bold text-amber-700">Largest Value Leak</div>
            <div className="mt-1 text-sm text-slate-700">
              <span className="font-semibold">Performance & Outcomes</span> is your lowest-scoring pillar at{' '}
              <span className="font-bold text-slate-900">75/100</span>. It caps how much value flows through the rest of the chain.
            </div>
            <div className="mt-2 text-2xl font-bold text-slate-900">$105K – $376K <span className="text-sm text-slate-500 font-normal">/year leaking</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// (B) CALM EXECUTIVE — left-border tiles, ribboned hero
// ─────────────────────────────────────────────────────────────
function CalmExecutiveVariant() {
  const colors = {
    healthy: { border: 'border-l-emerald-500', text: 'text-emerald-700', dot: 'bg-emerald-500' },
    risk: { border: 'border-l-amber-400', text: 'text-amber-700', dot: 'bg-amber-400' },
    critical: { border: 'border-l-rose-400', text: 'text-rose-700', dot: 'bg-rose-400' },
  };
  return (
    <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm bg-white">
      <div className="relative bg-white border-b border-slate-100 px-10 py-9">
        <div className="absolute top-0 bottom-0 left-0 w-1 bg-teal-500" />
        <div className="flex items-start justify-between gap-8">
          <div className="flex-1">
            <div className="text-[10px] tracking-widest text-teal-600 uppercase mb-2 font-semibold">Closed-loop performance system</div>
            <h1 className="text-3xl font-bold text-slate-900">Seertech</h1>
            <p className="mt-2 text-slate-600 max-w-3xl text-sm leading-relaxed">
              Identify workforce risk, model learning interventions, execute programs, and prove impact — in one continuous cycle.
            </p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">System health</div>
            <div className="mt-1 flex items-center justify-end gap-2">
              <span className="text-3xl font-bold text-slate-900 tabular-nums">73%</span>
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">Healthy</div>
          </div>
        </div>
        <div className="mt-6 flex items-center gap-1.5 text-xs">
          {VALUE_CHAIN.map((n, i) => (
            <React.Fragment key={n}>
              <span className={`px-2 py-0.5 rounded ${i === 5 ? 'text-rose-700 font-semibold bg-rose-50' : 'text-slate-600'}`}>{n}</span>
              {i < VALUE_CHAIN.length - 1 && <span className="text-slate-300">›</span>}
            </React.Fragment>
          ))}
        </div>
      </div>
      <div className="bg-slate-50/60 px-10 py-8">
        <div className="grid grid-cols-5 gap-4">
          {PILLARS.map(p => {
            const c = colors[p.state];
            const Icon = p.icon;
            return (
              <div key={p.label} className={`rounded-lg bg-white border border-slate-200 border-l-4 ${c.border} p-4`}>
                <div className="flex items-center justify-between">
                  <Icon className="w-4 h-4 text-slate-400" />
                  <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                </div>
                <div className="mt-3 text-[11px] text-slate-500 uppercase tracking-wider font-medium">{p.label}</div>
                <div className="mt-1 text-3xl font-bold text-slate-900 tabular-nums">{p.value}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">{p.sub}</div>
                <div className={`text-[11px] mt-2 font-medium ${p.delta.startsWith('-') ? 'text-rose-600' : 'text-emerald-600'}`}>{p.delta}</div>
              </div>
            );
          })}
        </div>
        <div className="mt-6 rounded-lg bg-white border border-slate-200 border-l-4 border-l-amber-400 p-5 flex items-start gap-4">
          <div className="shrink-0 w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] tracking-wider uppercase text-slate-500 font-semibold">Value Leak · attention</div>
            <div className="mt-1 text-sm text-slate-800">
              <span className="font-semibold">Performance & Outcomes</span> is the lowest-scoring pillar at{' '}
              <span className="font-bold text-slate-900">75/100</span> — it caps how much value flows through the rest of the chain.
            </div>
            <div className="mt-3 flex items-end gap-6">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Estimated impact</div>
                <div className="text-2xl font-bold text-slate-900 tabular-nums">$105K – $376K<span className="text-xs text-slate-500 font-normal ml-1">/yr</span></div>
              </div>
              <button className="text-xs font-medium text-teal-700 hover:text-teal-800 flex items-center gap-1 pb-1">
                See intervention <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// (C) MODERN ANALYTICAL — flat, type-led, no chrome
// ─────────────────────────────────────────────────────────────
function ModernAnalyticalVariant() {
  // Custom muted palette — single accent (teal), status via hairlines only
  const colors = {
    healthy: { ink: '#0f766e', accent: '#14b8a6' },
    risk: { ink: '#92400e', accent: '#d4a373' },
    critical: { ink: '#9f1239', accent: '#c97b84' },
  };
  return (
    <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm bg-white">
      <div className="bg-[#fafaf7] px-10 py-10 border-b" style={{ borderTopColor: '#14b8a6', borderTopWidth: 3 }}>
        <div className="flex items-end justify-between gap-12">
          <div className="flex-1">
            <div className="text-[10px] tracking-[0.2em] text-slate-500 uppercase font-medium mb-3">Seertech</div>
            <h1 className="font-serif text-[44px] leading-[1.05] text-slate-900 tracking-tight" style={{ fontFamily: 'Georgia, "Iowan Old Style", serif' }}>
              One continuous loop<br />
              <span className="text-slate-500 italic">from risk to return.</span>
            </h1>
          </div>
          <div className="shrink-0 text-right space-y-1">
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">System health</div>
            <div className="text-5xl font-light text-slate-900 tabular-nums tracking-tight">73<span className="text-2xl text-slate-400">%</span></div>
            <div className="inline-flex items-center gap-1.5 text-[11px] text-emerald-700">
              <span className="w-1 h-1 rounded-full bg-emerald-600" /> Healthy
            </div>
          </div>
        </div>
        <div className="mt-8 flex items-center gap-3 text-[11px] text-slate-500 font-medium">
          {VALUE_CHAIN.map((n, i) => (
            <React.Fragment key={n}>
              <span className={i === 5 ? 'text-slate-900 font-semibold' : ''}>{n}</span>
              {i < VALUE_CHAIN.length - 1 && <span className="text-slate-300">·</span>}
            </React.Fragment>
          ))}
        </div>
      </div>
      <div className="bg-white px-10 py-10">
        <div className="grid grid-cols-5 gap-0 border-l border-slate-100">
          {PILLARS.map(p => {
            const c = colors[p.state];
            return (
              <div key={p.label} className="border-r border-b border-t border-slate-100 px-5 py-6 relative">
                <div className="absolute top-0 left-0 h-0.5 w-8" style={{ backgroundColor: c.accent }} />
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-medium">{p.label}</div>
                <div className="mt-2 text-4xl font-light text-slate-900 tabular-nums tracking-tight">{p.value}</div>
                <div className="text-[11px] text-slate-400 mt-1">{p.sub}</div>
                <div className="mt-3 text-[11px] tabular-nums" style={{ color: p.delta.startsWith('-') ? '#9f1239' : '#0f766e' }}>{p.delta}</div>
              </div>
            );
          })}
        </div>
        <div className="mt-8 grid grid-cols-12 gap-6 items-start">
          <div className="col-span-1">
            <div className="w-8 h-8 rounded-full border border-amber-300 flex items-center justify-center">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
            </div>
          </div>
          <div className="col-span-7">
            <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-medium">Largest Value Leak</div>
            <div className="mt-1 text-base text-slate-900 leading-relaxed">
              <span className="font-medium">Performance & Outcomes</span> sits at 75/100 — the ceiling on every downstream metric in the chain.
            </div>
          </div>
          <div className="col-span-4 text-right">
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">Estimated leak</div>
            <div className="text-3xl font-light text-slate-900 tabular-nums tracking-tight mt-1">
              $105–376<span className="text-base text-slate-400 ml-0.5">K/yr</span>
            </div>
            <button className="mt-2 text-xs text-teal-700 inline-flex items-center gap-1 hover:gap-2 transition-all">
              Open intervention <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// LIGHT HERO — actual hero contents (closed-loop strip + system
// health pill + value chain) re-rendered in the lighter Option A
// language. Mirrors the LIE home hero structure 1:1 so it can be
// dropped in once approved.
// ─────────────────────────────────────────────────────────────
function LightHeroVariant() {
  const PHASES = [
    {
      phase: 'IDENTIFY', label: 'Identify Risk', desc: 'Where is value leaking?',
      accent: 'bg-rose-500', accentText: 'text-rose-600',
      tools: ['PE Health', 'ROE Health', 'LEI Health', 'Maturity Assessment'],
      Icon: Search,
    },
    {
      phase: 'MODEL', label: 'Model Intervention', desc: 'What would fix it?',
      accent: 'bg-blue-500', accentText: 'text-blue-600',
      tools: ['Solution Finder', 'ROE Modeller', 'Impact Analysis', 'Dampening What-If'],
      Icon: GitCompare,
    },
    {
      phase: 'EXECUTE', label: 'Execute', desc: 'Deliver the intervention',
      accent: 'bg-amber-500', accentText: 'text-amber-600',
      tools: ['Data Import', 'Program Analytics', 'PE Alerts', 'Score Trends'],
      Icon: Briefcase,
    },
    {
      phase: 'PROVE', label: 'Prove Impact', desc: 'Did it actually work?',
      accent: 'bg-emerald-500', accentText: 'text-emerald-600',
      tools: ['ROE Variance', 'Business Case Builder', 'Report Card', 'Correlation Analysis'],
      Icon: FileText,
    },
  ];

  return (
    <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm bg-white">
      {/* Hero — light slate-50 canvas, thin teal accent rule, subtle
          radial glow keeps brand identity without darkness. */}
      <div className="relative bg-slate-50 px-10 pt-9 pb-8 overflow-hidden border-b border-slate-200">
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-teal-400 via-teal-500 to-teal-400" />
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 18% 50%, #6366f1 0%, transparent 45%), ' +
              'radial-gradient(circle at 82% 50%, #14b8a6 0%, transparent 45%)',
          }}
        />

        <div className="relative z-10 flex items-start justify-between gap-6 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">
              Seertech
            </h1>
            <p className="mt-3 text-sm md:text-base text-slate-600 max-w-3xl leading-relaxed">
              Identify workforce risk, model learning interventions, execute programs, and prove impact — in one continuous cycle. Every metric connects to business outcomes through the value chain.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <button className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 text-xs font-medium transition-colors">
              <Pin className="w-3.5 h-3.5" /> Pin
            </button>
            <div className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-medium">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-slate-500 uppercase tracking-wider text-[10px]">System Health</span>
              <span className="font-bold text-slate-900 tabular-nums">73%</span>
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border border-emerald-200 text-emerald-700 bg-white">
                Healthy
              </span>
            </div>
          </div>
        </div>

        <div className="relative z-10 mt-1">
          <span className="inline-block text-[10px] font-semibold uppercase tracking-widest text-teal-600">
            The Missing Middle Navigation System
          </span>
        </div>

        {/* 4 phase tiles — white cards with phase-color top accent
            bar. Same density as today, just without the dark glass. */}
        <div className="relative z-10 mt-6 flex items-stretch gap-3 flex-wrap">
          {PHASES.map((step, i) => {
            const Icon = step.Icon;
            return (
              <React.Fragment key={step.phase}>
                <div className="relative bg-white border border-slate-200 rounded-lg px-4 py-3 min-w-[210px] flex-1 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                  <div className={`absolute top-0 left-0 right-0 h-0.5 ${step.accent}`} />
                  <div className="flex items-center gap-1.5 mb-1">
                    <Icon className={`w-3.5 h-3.5 ${step.accentText}`} />
                    <span className={`text-xs font-bold uppercase tracking-wider ${step.accentText}`}>{step.phase}</span>
                  </div>
                  <p className="text-sm font-semibold text-slate-900">{step.label}</p>
                  <p className="text-[10px] text-slate-500">{step.desc}</p>
                  <div className="mt-2 pt-2 border-t border-slate-100 space-y-0.5">
                    {step.tools.map(t => (
                      <div key={t} className="text-[10px] text-slate-500 hover:text-teal-700 cursor-pointer transition-colors leading-relaxed">
                        · {t}
                      </div>
                    ))}
                  </div>
                </div>
                {i < PHASES.length - 1 && <ArrowRight className="h-4 w-4 text-slate-300 self-center flex-shrink-0" />}
              </React.Fragment>
            );
          })}
          <ArrowRight className="h-4 w-4 text-slate-300 self-center flex-shrink-0" />
          <div className="self-center inline-flex items-center gap-1 text-xs text-teal-600 font-bold uppercase tracking-wider">
            <RotateCw className="w-3 h-3" /> Repeat
          </div>
        </div>

        {/* Value chain strip — light variant (already supported in the
            shared component; mocked inline here). */}
        <div className="relative z-10 mt-6 mx-auto max-w-fit">
          <div className="flex items-center gap-2 p-3 bg-white border border-slate-200 rounded-lg">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mr-1">Value chain</span>
            {VALUE_CHAIN.map((n, i) => (
              <React.Fragment key={n}>
                {i === 5 ? (
                  <span className="px-2.5 py-1 rounded-md text-xs bg-slate-900 text-white shadow-sm font-medium">{n}</span>
                ) : (
                  <span className="px-2.5 py-1 rounded-md text-xs bg-white border border-slate-200 text-slate-700">{n}</span>
                )}
                {i < VALUE_CHAIN.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-slate-300" />}
              </React.Fragment>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 text-center mt-1.5 italic">Step 6 of 8 — business outcomes</p>
        </div>
      </div>

      {/* Body — the already-shipped Option A pillar tiles, here for
          context so you see the hero <-> body relationship. */}
      <div className="bg-white px-10 py-8">
        <h2 className="text-lg font-bold text-slate-900 mb-1">Learning Impact Chain — Live Performance</h2>
        <p className="text-xs text-slate-500 mb-4">Each stage shows actual health from your data. Click any node to investigate.</p>
        <div className="grid grid-cols-5 gap-3">
          {PILLARS.map(p => {
            const Icon = p.icon;
            const ring = p.state === 'healthy' ? 'ring-emerald-200' : p.state === 'risk' ? 'ring-amber-200' : 'ring-rose-200';
            const text = p.state === 'healthy' ? 'text-emerald-700' : p.state === 'risk' ? 'text-amber-700' : 'text-rose-700';
            const dot = p.state === 'healthy' ? 'bg-emerald-500' : p.state === 'risk' ? 'bg-amber-500' : 'bg-rose-500';
            const border = p.state === 'healthy' ? 'border-emerald-200' : p.state === 'risk' ? 'border-amber-200' : 'border-rose-200';
            return (
              <div key={p.label} className="flex flex-col items-center">
                <div className={`w-full rounded-xl bg-white border border-slate-200 ring-1 ${ring} p-3 text-center shadow-sm`}>
                  <Icon className={`h-5 w-5 mx-auto mb-1 ${text}`} />
                  <p className="text-xs font-bold text-slate-700">{p.label}</p>
                  <p className={`text-lg font-extrabold mt-0.5 ${text}`}>{p.value}</p>
                  <p className="text-[9px] text-slate-400 mt-0.5">{p.sub}</p>
                  <span className={`text-[10px] font-bold mt-1 inline-flex items-center justify-center gap-0.5 px-1.5 py-0.5 rounded-full ${p.delta.startsWith('-') ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'}`}>
                    {p.delta.startsWith('-') ? '▼' : '▲'} {p.delta}
                  </span>
                </div>
                <div className={`mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold border bg-white ${border} ${text}`}>
                  <span className={`w-1 h-1 rounded-full ${dot}`} />
                  {p.state === 'healthy' ? 'Healthy' : p.state === 'risk' ? 'At Risk' : 'Critical'}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Page shell
// ─────────────────────────────────────────────────────────────
export default function LIEStylePreviewPage() {
  const variants = [
    { id: 'light-hero', label: 'NEW · Light Hero (to match Option A body)', summary: 'A light-themed take on the existing hero — slate-50 canvas with a thin teal accent rule, faint brand radial glow, white IDENTIFY/MODEL/EXECUTE/PROVE phase tiles with a phase-color top stripe, light System Health pill, and the value-chain strip in its light variant. Renders 1:1 the structure of today\'s hero so it can be swapped in once approved.', comp: LightHeroVariant },
    { id: 'current', label: 'CURRENT', summary: "Today's design — dark gradient hero, saturated alert tiles, triple-encoded status (border + fill + badge).", comp: CurrentVariant },
    { id: 'a', label: 'OPTION A · Soft Refresh (body shipped)', summary: 'Same structure. Light hero with a teal accent rule. White tiles with a soft outer ring per status. Smaller, outlined status pills. The body version of this is already live on the home page.', comp: SoftRefreshVariant },
    { id: 'b', label: 'OPTION B · Calm Executive', summary: 'White hero with a thin teal ribbon and a single system-health number. Tiles use a single 4px left-border to encode status. Weak link reads as a signal, not an emergency.', comp: CalmExecutiveVariant },
    { id: 'c', label: 'OPTION C · Modern Analytical', summary: 'Serif headline, type-led, single accent hairline per tile, light cream canvas. Status carried by a tiny color bar at the top of each tile. Most ambitious — also touches typography.', comp: ModernAnalyticalVariant },
  ];
  return (
    <div className="min-h-screen bg-slate-100 py-10 px-8">
      <div className="max-w-7xl mx-auto space-y-12">
        <header>
          <div className="text-[10px] tracking-widest text-slate-500 uppercase font-semibold">UX explorations</div>
          <h1 className="text-2xl font-bold text-slate-900 mt-1">Seertech — color scheme directions</h1>
          <p className="mt-2 text-sm text-slate-600 max-w-3xl">
            Static, data-stubbed previews of the home hero, value chain, pillar tiles, and weak-link callout. Use them to pick a direction — applying the chosen option to the live page is a separate step.
          </p>
        </header>
        {variants.map(v => {
          const Comp = v.comp;
          return (
            <section key={v.id} data-testid={`lie-preview-${v.id}`}>
              <div className="flex items-center gap-3 mb-3">
                <span className="text-[10px] tracking-widest font-bold text-slate-500 uppercase">{v.label}</span>
                <span className="h-px flex-1 bg-slate-200" />
              </div>
              <p className="text-sm text-slate-600 mb-4 flex items-start gap-2 max-w-3xl">
                <Info className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                <span>{v.summary}</span>
              </p>
              <Comp />
            </section>
          );
        })}
      </div>
    </div>
  );
}
